import {
    ComparisonField,
    CurriculumFinderConfig,
    CurriculumType,
    CurriculumTypeDynamicFilter,
    HydratedComparisonField,
    HydratedCurriculumType,
    HydratedCurriculumTypeDynamicFilter,
    HydratedLinkTarget,
    LinkTarget,
} from '../Common/types/AppConfigTypes';
import { ContentType, Field } from '../Common/types/CatalogTypes';

export const combineAppConfigWithContentTypes = (
    CFConfig: CurriculumFinderConfig,
    contentTypes: ContentType[],
): HydratedCurriculumType[] => {
    const curriculumTypes = CFConfig.curriculumTypes.map(
        (curriculumType, index): HydratedCurriculumType | undefined =>
            hydrateCurriculumType(curriculumType, index, contentTypes),
    );

    return curriculumTypes.filter(
        (
            ct: HydratedCurriculumType | undefined,
        ): ct is HydratedCurriculumType => notUndefined(ct),
    );
};

export const hydrateCurriculumType = (
    curriculumType: CurriculumType,
    index: number,
    contentTypes: ContentType[],
): HydratedCurriculumType | undefined => {
    const {
        displayName,
        displayNamePlural,
        slug,
        isAZ,
        contentTypeUuid,
        listView,
        comparisonView,
    } = curriculumType;

    const contentType = findContentTypeByUuid(contentTypeUuid, contentTypes);
    const filterResults = curriculumType.filters.map((filter, index) =>
        replaceFilterUuidsWithContentTypes(filter, index, contentTypes),
    );
    const filters: HydratedCurriculumTypeDynamicFilter[] = filterResults.filter(
        (
            fil: HydratedCurriculumTypeDynamicFilter | undefined,
        ): fil is HydratedCurriculumTypeDynamicFilter => notUndefined(fil),
    );

    const listViewHeader = replaceLinkTargetUuids(
        listView.header,
        contentTypes,
    );
    const listViewDescription = replaceLinkTargetUuids(
        listView.description,
        contentTypes,
    );
    const comparisonViewName = replaceLinkTargetUuids(
        comparisonView.name,
        contentTypes,
    );
    const comparisonFieldResults = comparisonView.comparisonFields.map(cf =>
        hydrateComparisonField(cf, contentTypes),
    );
    const comparisonFields = comparisonFieldResults.filter(
        (
            cf: HydratedComparisonField | undefined,
        ): cf is HydratedComparisonField => notUndefined(cf),
    );

    if (
        !contentType ||
        !listViewHeader ||
        !listViewDescription ||
        !comparisonViewName
    ) {
        console.error('Problem hydrating curriculum type...');
        return;
    }

    return {
        index,
        displayName,
        displayNamePlural,
        slug,
        isAZ,
        contentType,
        filters,
        listView: {
            header: listViewHeader,
            description: listViewDescription,
        },
        comparisonView: {
            name: comparisonViewName,
            comparisonFields,
        },
    };
};

export const replaceFilterUuidsWithContentTypes = (
    filter: CurriculumTypeDynamicFilter,
    filterIndex: number,
    contentTypes: ContentType[],
): HydratedCurriculumTypeDynamicFilter | undefined => {
    const { name, control } = filter;
    const linkedTo = replaceLinkTargetUuids(filter.linkedTo, contentTypes);

    if (!linkedTo) {
        return undefined;
    }

    return {
        name,
        index: filterIndex,
        control,
        linkedTo,
    };
};

export const hydrateComparisonField = (
    comparisonField: ComparisonField,
    contentTypes: ContentType[],
): HydratedComparisonField | undefined => {
    const contentType = findContentTypeByUuid(
        comparisonField.contentTypeUuid,
        contentTypes,
    );

    const field = findFieldByUuid(
        comparisonField.fieldUuid,
        comparisonField.contentTypeUuid,
        contentTypes,
    );

    if (!contentType || !field) {
        console.error('Hydrate comparison field failed.');
        return;
    }

    return {
        displayName: comparisonField.displayName,
        contentType,
        field,
    };
};

export const replaceLinkTargetUuids = (
    linkTarget: LinkTarget,
    contentTypes: ContentType[],
): HydratedLinkTarget | undefined => {
    const contentType = findContentTypeByUuid(
        linkTarget.contentTypeUuid,
        contentTypes,
    );
    const field = findFieldByUuid(
        linkTarget.fieldUuid,
        linkTarget.contentTypeUuid,
        contentTypes,
    );

    if (!contentType || !field) {
        console.error('Could not find content type or field');
        return;
    }

    return {
        contentType,
        field,
    };
};

const findContentTypeByUuid = (
    contentTypeUuid: string,
    contentTypes: ContentType[],
): ContentType | undefined => {
    const match = contentTypes.find(ct => ct.uuid === contentTypeUuid);

    if (!match) {
        throw new Error(
            `Could not find content type with uuid: ${contentTypeUuid}`,
        );
    }

    return match;
};

const findFieldByUuid = (
    fieldUuid: string,
    contentTypeUuid: string,
    contentTypes: ContentType[],
): Field | undefined => {
    const matchingContentType = findContentTypeByUuid(
        contentTypeUuid,
        contentTypes,
    );

    const matchingField =
        matchingContentType &&
        matchingContentType.fields.find(f => f.uuid === fieldUuid);

    if (!matchingField) {
        throw new Error(`Could not find field with uuid: ${fieldUuid}`);
    }

    return matchingField;
};

function notUndefined<T>(x: T | undefined): x is T {
    return x !== undefined;
}
